/**
 * useBrandSpaceUsage
 *
 * Brand Space allowance of the current Brand Portfolio (GET /api/teams/package -> usage.brandSpaces).
 * Returns null while loading or when the backend does not report it (older response / non-portfolio user),
 * so callers can fall back gracefully.
 */

import { useQuery } from '@tanstack/react-query';
import { packageService } from '@/services/package.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import type { BrandSpaceUsage } from '@/types/package';


export const useBrandSpaceUsage = (enabled: boolean = true): BrandSpaceUsage | null => {
  const { teamId, hasTeam, isAgencyStaff } = useEffectiveTeamId();
  // Same endpoint + key as usePackage: shares its cache (no duplicate fetch), scoped per effective team (God Mode safe).
  // Team-less agency staff/owners (no home team, not in God Mode) use the ['no-team'] key; the backend resolves
  // their acting agency when no team context exists.
  const { data } = useQuery({
    queryKey: teamQueryKeys.package.team(teamId),
    queryFn: packageService.getTeamPackage,
    staleTime: 30 * 1000,
    retry: false,
    enabled: (hasTeam || isAgencyStaff) && enabled,
    select: (response) => response?.usage.brandSpaces ?? null,
  });
  return data ?? null;
};
